import BaseMesh from "./Base/BaseMesh";
import { vectorizeGenomicPosInd } from "../../helpers/vectorize";

function calcDefaultOffsets(uniforms, isRaycasting) {
  const conf = uniforms.getMeshConf();
  const px = conf.paddingX;
  const py = conf.paddingY;
  const o = conf.offset;

  const normalBorder = conf.borderWidth || 1;
  const raycastBorder = conf.raycastBorderWidth || 1;

  const b = isRaycasting ? raycastBorder : normalBorder;

  if (!px || !py || !o || !b) return;
  const [px1, px2, px3, px4] = px.map((x) => x + o[0]);
  const [py1, py2, py3, py4] = py.map((x) => x + o[1]);

  const p1 = [px1, py1]; // left    top
  const p2 = [px2, py2]; // right   top
  const p3 = [px3, py3]; // left    bottom
  const p4 = [px4, py4]; // right   bottom

  const p1i = [px1 + b, py1 + b]; //  left    top     internal
  const p2i = [px2 - b, py2 + b]; //  right   top     internal
  const p3i = [px3 + b, py3 - b]; //  left    bottom  internal
  const p4i = [px4 - b, py4 - b]; //  right   bottom  internal

  const res = [...p1, ...p1i, ...p2, ...p2i, ...p4, ...p4i, ...p3, ...p3i];

  uniforms.setOffsetXMap(res.filter((_, i) => i % 2 === 0));
  uniforms.setOffsetYMap(res.filter((_, i) => i % 2 === 1));
}

function updateBufferUnary(buffAttr, { count, isAppend }) {
  const { step, buffer } = buffAttr;
  const { array } = buffer;
  const start = isAppend ? buffAttr.itemCount : 0;

  buffAttr.updateItemCount(count, isAppend);

  return (i, y1, y2, y3, y4) => {
    const base = (start + i) * step;
    array[base + 0] = y1;
    array[base + 1] = y1;
    array[base + 2] = y2;
    array[base + 3] = y2;
    array[base + 4] = y4;
    array[base + 5] = y4;
    array[base + 6] = y3;
    array[base + 7] = y3;
  };
}

class RectangleOutlineMesh extends BaseMesh {
  constructor(uniforms, conf) {
    const verticesPerItem = 8;
    const idxPerItem = 24;
    const idxPattern = [
      ...[0, 1, 2, 1, 3, 2], // rect top
      ...[3, 5, 2, 5, 4, 2], // rect right
      ...[6, 4, 7, 7, 4, 5], // rect bottom
      ...[7, 1, 6, 1, 0, 6], // rect left
    ];

    calcDefaultOffsets(uniforms);

    super({ uniforms, conf, verticesPerItem, idxPattern, idxPerItem });
  }

  onStartRaycast() {
    calcDefaultOffsets(this.uniforms, true);
  }

  onEndRaycast() {
    calcDefaultOffsets(this.uniforms, false);
  }

  updateX({ count, isAppend }) {
    const posXBuffer = this.attrs.posX;
    const { step, buffer } = posXBuffer;
    const { array } = buffer;
    const start = isAppend ? posXBuffer.itemCount : 0;
    posXBuffer.updateItemCount(count, isAppend);

    return (i, x1, x2, x3, x4) => {
      const base = (start + i) * step;
      vectorizeGenomicPosInd(array, base + 0 * 2, x1);
      vectorizeGenomicPosInd(array, base + 1 * 2, x1);
      vectorizeGenomicPosInd(array, base + 2 * 2, x2);
      vectorizeGenomicPosInd(array, base + 3 * 2, x2);
      vectorizeGenomicPosInd(array, base + 4 * 2, x4);
      vectorizeGenomicPosInd(array, base + 5 * 2, x4);
      vectorizeGenomicPosInd(array, base + 6 * 2, x3);
      vectorizeGenomicPosInd(array, base + 7 * 2, x3);
    };
  }

  updateRgba(args) {
    return updateBufferUnary(this.attrs.rgbaCode, args);
  }

  updatePosYInd(args) {
    return updateBufferUnary(this.attrs.posYInd, args);
  }

  updateY(args) {
    return updateBufferUnary(this.attrs.posY, args);
  }
}

export default RectangleOutlineMesh;
